import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Collapse, TableCell, TableRow } from '@mui/material';

import OnFileDownloadIcon from '../../../icons/OnFileDownloadIcon';
import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { downloadParticipantDocument } from '../../../store/documentService';
import { loadParticipantByPtCode } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { hasRole } from '../../../utils/userRoles';

import DarDetails from './DarDetails';

import classes from '../../../commonStyles/tables.module.css';
import darClasses from '../homeStyles/dar.module.css';

const expandedCellStyles = {
    paddingBottom: 0,
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    borderTop: 'none',
    borderBottom: 'none'
};

const DarTableRow = ({
    row,
    acceptedRoles,
    userRoles,
    onUpdateDarRow,
    toggleDropdown,
    openId
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USEEFFECTS
    useEffect(() => {
        if (isPTRequestSent && currentParticipant?.ptCode === row.ptCode) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isPTRequestSent, currentParticipant]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === row.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onClickPTCode = () => {
        if (!row?.ptCode) return;
        if (currentParticipant.ptCode !== row.ptCode) dispatch(loadParticipantByPtCode(row.ptCode));
        setIsPTRequestSent(true);
    };

    const onClickDarCode = () => {
        if (!row?.statusId) return;
        onUpdateDarRow(row);
    };

    const onToggleDropdown = () => {
        toggleDropdown(openId === row.id ? '' : row.id);
    };

    const onDownload = () => {
        row.documents.forEach((el) =>
            dispatch(downloadParticipantDocument(row.participantId, el.filename))
        );
    };

    // RENDER
    return (
        <>
            <TableRow
                key={row.id}
                hover
                className={`${classes.tableRowStripes} ${classes.canOpen}`}>
                <TableCell onClick={onClickDarCode} disabled={!hasRole(acceptedRoles, roles)}>
                    <span className={darClasses.darCodeLink}>{row.code}</span>
                </TableCell>
                <TableCell onClick={onToggleDropdown}>{row.serviceName || ' - '}</TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.dateCreated)}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.dateUpdated)}
                </TableCell>
                <TableCell onClick={onClickPTCode} disabled={!hasRole(acceptedRoles, roles)}>
                    <span className={darClasses.darCodeLink}>{row?.ptCode}</span>
                </TableCell>
                <TableCell onClick={onToggleDropdown}>{row.priorityName}</TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {row.statusName.toLowerCase() === 'zzclosed'
                        ? row.statusName.substring(2, 8)
                        : row.statusName}
                </TableCell>
                <TableCell>
                    {row.documents?.length ? (
                        <OnFileDownloadIcon
                            active={hasRole(acceptedRoles, userRoles)}
                            onDownload={onDownload}
                        />
                    ) : (
                        ''
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={expandedCellStyles} colSpan={8}>
                    <Collapse in={openId === row.id} timeout="auto" unmountOnExit>
                        <DarDetails
                            row={row}
                            onClose={onToggleDropdown}
                        />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

DarTableRow.propTypes = {
    row: PropTypes.object,
    userRoles: PropTypes.arrayOf(PropTypes.string),
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    onUpdateDarRow: PropTypes.func,
    toggleDropdown: PropTypes.func,
    openId: PropTypes.string
};

export default DarTableRow;
